import { api } from './api';

export type ItemAgenda = {
  tipo: 'evento' | 'entrega';
  id: number;
  titulo: string;
  descricao: string | null;
  local: string | null;
  inicio: string;
  fim: string;
  publico: boolean;
  cancelado: boolean;
  minhaEscala: string | null;
  categoria?: string | null;
};

/** As cores do calendario, para ver de longe o que e cada coisa. */
export const CORES_AGENDA = {
  aniversario: { rotulo: 'Aniversário da igreja', etiqueta: 'bg-yellow-400 text-tinta-900', borda: 'border-yellow-400' },
  fe: { rotulo: 'Culto da Fé', etiqueta: 'bg-violet-600 text-white', borda: 'border-violet-600' },
  arena: { rotulo: 'Arena', etiqueta: 'bg-orange-600 text-white', borda: 'border-orange-600' },
  domingo: { rotulo: 'Culto de Domingo', etiqueta: 'bg-sky-600 text-white', borda: 'border-sky-600' },
  culto: { rotulo: 'Outro culto', etiqueta: 'bg-tinta-900 text-white', borda: 'border-tinta-900' },
  especial: { rotulo: 'Programação especial', etiqueta: 'bg-emerald-600 text-white', borda: 'border-emerald-600' },
  interno: { rotulo: 'Reunião, ensaio e limpeza', etiqueta: 'bg-slate-200 text-tinta-700', borda: 'border-slate-300' },
  entrega: { rotulo: 'Entrega da mídia', etiqueta: 'bg-amber-100 text-amber-800', borda: 'border-amber-300' },
} as const;

const semAcento = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function corDoItem(e: Pick<ItemAgenda, 'tipo' | 'titulo' | 'inicio' | 'publico' | 'categoria'>) {
  if (e.tipo === 'entrega') return CORES_AGENDA.entrega;
  if (semAcento(e.titulo).includes('aniversario')) return CORES_AGENDA.aniversario;
  const categoria = e.categoria ?? 'culto';
  if (categoria === 'especial') return CORES_AGENDA.especial;
  if (categoria === 'ensaio' || categoria === 'reuniao') return CORES_AGENDA.interno;
  // culto: a cor sai do dia da semana (terca = Fe, sabado = Arena, domingo = Domingo)
  const dia = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', weekday: 'short' }).format(new Date(e.inicio));
  if (dia === 'Tue') return CORES_AGENDA.fe;
  if (dia === 'Sat') return CORES_AGENDA.arena;
  if (dia === 'Sun') return CORES_AGENDA.domingo;
  return e.publico ? CORES_AGENDA.culto : CORES_AGENDA.interno;
}

export const buscarAgenda = (mes: string) =>
  api<{ itens: ItemAgenda[]; podeEditar: boolean }>(`/agenda?mes=${mes}`);

export const buscarLinkAgenda = () => api<{ caminho: string }>('/agenda/link');
export const gerarLinkAgenda = () => api<{ caminho: string }>('/agenda/link', { method: 'POST' });

export const criarEvento = (dados: Record<string, unknown>) =>
  api<{ id: number }>('/eventos', { method: 'POST', body: JSON.stringify(dados) });

export const apagarEvento = (id: number) => api(`/eventos/${id}`, { method: 'DELETE' });

/** "YYYY-MM" do mes corrente em Sao Paulo. */
export function mesAtual(d = new Date()): string {
  const p = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
  }).format(d);
  return p.slice(0, 7);
}

export function somarMeses(mes: string, n: number): string {
  const [ano, m] = mes.split('-').map(Number);
  const total = (ano as number) * 12 + (m as number) - 1 + n;
  const novoAno = Math.floor(total / 12);
  const novoMes = (total % 12) + 1;
  return `${novoAno}-${String(novoMes).padStart(2, '0')}`;
}

export function nomeDoMes(mes: string): string {
  const [ano, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(ano as number, (m as number) - 1, 15, 12));
  const texto = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(d);
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** Dia "YYYY-MM-DD" (em Sao Paulo) de um instante ISO. */
export function diaDe(iso: string): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function horaDe(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Grade do mes comecando no domingo, com os dias vizinhos para completar. */
export function gradeDoMes(mes: string): { dia: string; doMes: boolean }[] {
  const [ano, m] = mes.split('-').map(Number);
  const primeiro = new Date(Date.UTC(ano as number, (m as number) - 1, 1, 12));
  const inicio = new Date(primeiro);
  inicio.setUTCDate(inicio.getUTCDate() - primeiro.getUTCDay());

  const dias: { dia: string; doMes: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(inicio);
    d.setUTCDate(inicio.getUTCDate() + i);
    const texto = d.toISOString().slice(0, 10);
    dias.push({ dia: texto, doMes: texto.slice(0, 7) === mes });
    if (i >= 34 && texto.slice(0, 7) !== mes && dias.length % 7 === 0) break;
  }
  return dias;
}

export const HOJE_SP = () =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

/* --------------------------- repeticao semanal --------------------------- */

export type Serie = {
  id: number;
  titulo: string;
  tipo: string;
  descricao: string | null;
  local: string | null;
  diaSemana: number;
  hora: string;
  duracaoMin: number;
  publico: boolean;
  ativo: boolean;
  geradoAte: string | null;
  proximos: number;
};

export const DIAS_DA_SEMANA = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
] as const;

export const buscarSeries = () =>
  api<{ series: Serie[]; podeEditar: boolean }>('/series');

export const criarSerie = (dados: {
  titulo: string;
  tipo?: string;
  local?: string | null;
  diaSemana: number;
  hora: string;
  duracaoMin?: number;
  publico?: boolean;
  semanas?: number;
}) => api<{ id: number; gerados: number }>('/series', { method: 'POST', body: JSON.stringify(dados) });

export const salvarSerie = (
  id: number,
  mudancas: {
    titulo?: string;
    local?: string | null;
    diaSemana?: number;
    hora?: string;
    publico?: boolean;
    ativo?: boolean;
  },
) => api(`/series/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });

export const gerarMaisSemanas = (id: number, semanas: number) =>
  api<{ gerados: number; dias: string[] }>(`/series/${id}/gerar`, {
    method: 'POST',
    body: JSON.stringify({ semanas }),
  });

export const apagarSerie = (id: number) =>
  api<{ eventosMantidos: number }>(`/series/${id}`, { method: 'DELETE' });

/** "12 de outubro" — ate onde a agenda desta serie ja esta preenchida. */
export function ateQuandoLegivel(dia: string | null): string | null {
  if (!dia) return null;
  const [ano, mes, d] = dia.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' }).format(
    new Date(Date.UTC(ano, mes - 1, d)),
  );
}

/* -------------------------- dias sem culto -------------------------- */

export type Feriado = { id: number; dia: string; nome: string; semCulto: boolean };

export const buscarFeriados = () =>
  api<{ feriados: Feriado[]; podeEditar: boolean }>('/feriados');

export const criarFeriado = (dados: { dia: string; nome: string; semCulto?: boolean }) =>
  api<{ id: number; eventosNesseDia: number }>('/feriados', {
    method: 'POST',
    body: JSON.stringify(dados),
  });

export const apagarFeriado = (id: number) => api(`/feriados/${id}`, { method: 'DELETE' });
